import React, { useEffect, useState, useRef, Component } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { GlobeIcon, SearchIcon, RefreshCwIcon, ChevronDownIcon, KeyIcon, EyeIcon, EyeOffIcon, CheckCircleIcon, XCircleIcon, BarChart2Icon, LinkIcon, XIcon, LockIcon, ServerIcon, DatabaseIcon, CreditCardIcon, UsersIcon, FileTextIcon, WalletIcon, ReceiptIcon, BanknoteIcon, SettingsIcon, CheckIcon, CopyIcon, PlusIcon } from 'lucide-react';
import { getTenant } from '../../../theme/tokens';
interface OAuth2App {
  id: string;
  name: string;
  logo: string;
  publicKey: string;
  privateKey: string;
  scopes: string[];
  status: 'active' | 'inactive';
  transactionVolume: number;
  transactionCount: number;
  domains: string[];
  connectedDate: string;
  lastUsed: string;
  expiresAt?: string | null;
}
type ExpirationPreset = 'never' | '30d' | '60d' | '90d' | '180d' | '1y' | 'custom';
const EXPIRATION_PRESETS: Array<{
  value: ExpirationPreset;
  label: string;
}> = [{
  value: 'never',
  label: 'Never expires'
}, {
  value: '30d',
  label: '30 days'
}, {
  value: '60d',
  label: '60 days'
}, {
  value: '90d',
  label: '90 days'
}, {
  value: '180d',
  label: '6 months'
}, {
  value: '1y',
  label: '1 year'
}, {
  value: 'custom',
  label: 'Custom date…'
}];
function presetToIsoDate(preset: ExpirationPreset, customDate: string): string | null {
  if (preset === 'never') return null;
  if (preset === 'custom') return customDate || null;
  const days: Record<Exclude<ExpirationPreset, 'never' | 'custom'>, number> = {
    '30d': 30,
    '60d': 60,
    '90d': 90,
    '180d': 180,
    '1y': 365
  };
  const d = new Date();
  d.setDate(d.getDate() + days[preset as Exclude<ExpirationPreset, 'never' | 'custom'>]);
  return d.toISOString().split('T')[0];
}
function formatExpiration(iso: string): {
  label: string;
  tone: 'normal' | 'soon' | 'expired';
} {
  const target = new Date(iso);
  const now = new Date();
  const msPerDay = 1000 * 60 * 60 * 24;
  const diffDays = Math.ceil((target.getTime() - now.getTime()) / msPerDay);
  const formatted = target.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
  if (diffDays < 0) return {
    label: `Expired ${formatted}`,
    tone: 'expired'
  };
  if (diffDays <= 14) return {
    label: `Expires ${formatted} (${diffDays}d)`,
    tone: 'soon'
  };
  return {
    label: `Expires ${formatted}`,
    tone: 'normal'
  };
}
const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors placeholder:text-gray-400';
const scopeConfig: Record<string, {
  name: string;
  icon: React.ReactNode;
}> = {
  transactions: {
    name: 'Transactions',
    icon: <CreditCardIcon size={12} className="text-[#9F140F]" data-id="element-14667" data-mp="3pss38c5p5uir" />
  },
  refunds: {
    name: 'Refunds',
    icon: <RefreshCwIcon size={12} className="text-yellow-700" aria-hidden="true" data-id="element-14668" data-mp="149l77oc4oe5" />
  },
  company_info: {
    name: 'Company Info',
    icon: <UsersIcon size={12} className="text-green-600" data-id="element-14669" data-mp="avnegxpp0ryu" />
  },
  customer_data: {
    name: 'Customer Data',
    icon: <DatabaseIcon size={12} className="text-purple-600" data-id="element-14670" data-mp="ftyq9dzjtcjp" />
  },
  suppliers: {
    name: 'Suppliers',
    icon: <ServerIcon size={12} className="text-blue-500" data-id="element-14671" data-mp="3edxhiev5hup0" />
  },
  bills: {
    name: 'Bills',
    icon: <FileTextIcon size={12} className="text-rose-500" data-id="element-14672" data-mp="3jci7llgouxzq" />
  },
  invoices: {
    name: 'Invoices',
    icon: <ReceiptIcon size={12} className="text-teal-600" data-id="element-14673" data-mp="3oa0kfmxttxd0" />
  },
  balance: {
    name: 'Balance',
    icon: <WalletIcon size={12} className="text-emerald-600" data-id="element-14674" data-mp="3tao7xymujw2h" />
  },
  settlements: {
    name: 'Settlements',
    icon: <BanknoteIcon size={12} className="text-indigo-500" data-id="element-14675" data-mp="13k2vfpsdxkct" />
  },
  analytics: {
    name: 'Analytics',
    icon: <BarChart2Icon size={12} className="text-amber-500" data-id="element-14676" data-mp="18hulsfddsyew" />
  },
  settings: {
    name: 'Settings',
    icon: <SettingsIcon size={12} className="text-gray-500" data-id="element-14677" data-mp="1673gpg4pufik" />
  }
};
const defaultApps: OAuth2App[] = [{
  id: 'oauth-001',
  name: 'Shopify',
  logo: 'https://cdn.shopify.com/s/files/1/0533/2089/files/Shopify_logo_small.png?v=1512152678',
  publicKey: 'pub_key_6a8b4c2d3e7f9g0h1i2j',
  privateKey: 'prv_key_5f4e3d2c1b0a9z8y7x6w5v4u3t2s1r',
  scopes: ['transactions', 'refunds', 'company_info'],
  status: 'active',
  transactionVolume: 87500,
  transactionCount: 1243,
  domains: ['mystore.shopify.com', 'checkout.shopify.com'],
  connectedDate: '2023-05-12',
  lastUsed: '2 hours ago'
}, {
  id: 'oauth-002',
  name: 'Opera PMS',
  logo: 'https://www.oracle.com/a/ocom/img/cb71-opera-cloud-services.jpg',
  publicKey: 'pub_key_7b9c5d3e1f8g2h4i6j0k',
  privateKey: 'prv_key_6g5f4e3d2c1b0a9z8y7x6w5v4u3t',
  scopes: ['transactions', 'company_info'],
  status: 'active',
  transactionVolume: 125400,
  transactionCount: 2156,
  domains: ['operahotel.com', 'api.operacloud.com'],
  connectedDate: '2023-02-28',
  lastUsed: '5 minutes ago'
}, {
  id: 'oauth-003',
  name: 'CloudBeds',
  logo: 'https://assets.cloudbeds.com/static/images/cloudbeds-logo.svg',
  publicKey: 'pub_key_8c0d6e4f2g9h1i3j5k7l',
  privateKey: 'prv_key_7h6g5f4e3d2c1b0a9z8y7x6w5v',
  scopes: ['transactions', 'refunds', 'customer_data'],
  status: 'active',
  transactionVolume: 65800,
  transactionCount: 954,
  domains: ['app.cloudbeds.com', 'api.cloudbeds.com'],
  connectedDate: '2023-08-15',
  lastUsed: '1 day ago'
}, {
  id: 'oauth-004',
  name: 'Mews Systems',
  logo: 'https://mews-careers.cdn.prismic.io/mews-careers/a84d4ff0-6b90-44a2-9f7e-c5b511ef17d2_mews-logo.svg',
  publicKey: 'pub_key_9d1e7f5g3h0i2j4k6l8m',
  privateKey: 'prv_key_8i7h6g5f4e3d2c1b0a9z8y7x6w',
  scopes: ['transactions', 'company_info', 'settlements'],
  status: 'inactive',
  transactionVolume: 32400,
  transactionCount: 567,
  domains: ['mews.com', 'api.mews.com'],
  connectedDate: '2023-04-10',
  lastUsed: '3 weeks ago'
}, {
  id: 'oauth-006',
  name: 'Salesforce',
  logo: 'https://www.salesforce.com/content/dam/sfdc-docs/www/logos/logo-salesforce.svg',
  publicKey: 'pub_key_1f3g5h7i9j0k2l4m6n8o',
  privateKey: 'prv_key_0k9j8i7h6g5f4e3d2c1b0a9z8y',
  scopes: ['company_info', 'customer_data'],
  status: 'active',
  transactionVolume: 0,
  transactionCount: 0,
  domains: ['salesforce.com', 'force.com'],
  connectedDate: '2023-09-05',
  lastUsed: '3 days ago'
}, {
  id: 'oauth-008',
  name: 'Xero Accounting',
  logo: 'https://www.xero.com/content/dam/xero/pilot-images/brand/logos/logo-xero-blue.svg',
  publicKey: 'pub_key_3h5i7j9k1l2m4n6o8p0q',
  privateKey: 'prv_key_2m1l0k9j8i7h6g5f4e3d2c1b0a',
  scopes: ['transactions', 'company_info'],
  status: 'active',
  transactionVolume: 0,
  transactionCount: 0,
  domains: ['xero.com', 'api.xero.com'],
  connectedDate: '2023-07-22',
  lastUsed: '12 hours ago'
}];
function StatusDropdown({
  value,
  onChange,
  options
}: {
  value: string;
  onChange: (v: string) => void;
  options: Array<{
    value: string;
    label: string;
    dot?: string;
  }>;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const selected = options.find(o => o.value === value);
  return <div ref={ref} className="relative" data-id="element-14678" data-mp="38j7p222ecys3">
      <button type="button" aria-expanded={open} aria-haspopup="listbox" onClick={() => setOpen(!open)} className="h-[42px] px-3 pr-8 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors flex items-center gap-2 min-w-[150px] focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-14679" data-mp="196lr5l3bzy55">
        {selected?.dot && <span className={`w-2 h-2 rounded-full ${selected.dot} flex-shrink-0`} data-id="element-14680" data-mp="3pssegg7v2x4e" />}
        <span className="truncate" data-id="element-14681" data-mp="3ktp88sg5akuu">{selected?.label}</span>
        <ChevronDownIcon size={14} className={`absolute right-2.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-14682" data-mp="3kwbf0pv7owc0" />
      </button>

      {open && <div className="absolute z-20 mt-1 right-0 w-48 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden" data-id="element-14683" data-mp="2s7ecbv3hh84d">
          {options.map(opt => <button key={opt.value} type="button" onClick={() => {
        onChange(opt.value);
        setOpen(false);
      }} className={`w-full px-3 py-2.5 text-left text-sm flex items-center justify-between hover:bg-gray-50 transition-colors ${value === opt.value ? 'bg-[#E2231A]/5 font-medium' : 'text-gray-700'}`} data-id="element-14684" data-mp="322ocsee8h7g1">
              <div className="flex items-center gap-2" data-id="element-14685" data-mp="txff3to6ijj2">
                {opt.dot && <span className={`w-2 h-2 rounded-full ${opt.dot} flex-shrink-0`} data-id="element-14686" data-mp="fxed2aw6h620" />}
                <span data-id="element-14687" data-mp="jzhqh52eqkpx">{opt.label}</span>
              </div>
              {value === opt.value && <CheckIcon size={14} className="text-[#9F140F] flex-shrink-0" data-id="element-14688" data-mp="1cmcay1z85k3e" />}
            </button>)}
        </div>}
    </div>;
}
const getStatusBadge = (status: 'active' | 'inactive') => {
  const styles = {
    active: 'bg-green-50 text-green-700',
    inactive: 'bg-red-50 text-red-700'
  };
  const icons = {
    active: <CheckCircleIcon size={11} className="mr-1" data-id="element-14689" data-mp="1qlbooel78eks" />,
    inactive: <XCircleIcon size={11} className="mr-1" data-id="element-14690" data-mp="1vcldxe2u5fw5" />
  };
  const labels = {
    active: 'Active',
    inactive: 'Inactive'
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${styles[status]}`} data-id="element-14691" data-mp="n6504g9i6rpg">
      {icons[status]}
      {labels[status]}
    </span>;
};
function generateKey(prefix: string) {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let key = `${prefix}_`;
  for (let i = 0; i < 24; i++) key += chars[Math.floor(Math.random() * chars.length)];
  return key;
}
export function Connect() {
  const {
    t
  } = useTranslation('terminals');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showPrivateKeys, setShowPrivateKeys] = useState<Record<string, boolean>>({});
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [apps, setApps] = useState<OAuth2App[]>(defaultApps);
  const [showAddModal, setShowAddModal] = useState(false);
  const [pendingToggle, setPendingToggle] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newDomains, setNewDomains] = useState('');
  const [newScopes, setNewScopes] = useState<string[]>([]);
  const [newExpirationPreset, setNewExpirationPreset] = useState<ExpirationPreset>('never');
  const [newCustomExpirationDate, setNewCustomExpirationDate] = useState('');
  const [editingApp, setEditingApp] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDomains, setEditDomains] = useState('');
  const [editScopes, setEditScopes] = useState<string[]>([]);
  const togglePrivateKey = (appId: string) => {
    setShowPrivateKeys(prev => ({
      ...prev,
      [appId]: !prev[appId]
    }));
  };
  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(id);
    setTimeout(() => setCopiedKey(null), 1500);
  };
  const requestToggle = (appId: string) => {
    setPendingToggle(appId);
  };
  const confirmToggle = () => {
    if (!pendingToggle) return;
    setApps(prev => prev.map(app => app.id === pendingToggle ? {
      ...app,
      status: app.status === 'active' ? 'inactive' : 'active'
    } : app));
    setPendingToggle(null);
  };
  const pendingApp = pendingToggle ? apps.find(a => a.id === pendingToggle) : null;
  const openEditModal = (appId: string) => {
    const app = apps.find(a => a.id === appId);
    if (!app) return;
    setEditingApp(appId);
    setEditName(app.name);
    setEditDomains(app.domains.join(', '));
    setEditScopes([...app.scopes]);
  };
  const toggleEditScope = (scopeId: string) => {
    setEditScopes(prev => prev.includes(scopeId) ? prev.filter(s => s !== scopeId) : [...prev, scopeId]);
  };
  const handleSaveEdit = () => {
    if (!editingApp || !editName.trim()) return;
    const domains = editDomains.split(',').map(d => d.trim()).filter(Boolean);
    setApps(prev => prev.map(app => app.id === editingApp ? {
      ...app,
      name: editName.trim(),
      domains: domains.length > 0 ? domains : app.domains,
      scopes: editScopes.length > 0 ? editScopes : app.scopes
    } : app));
    setEditingApp(null);
  };
  const toggleScope = (scopeId: string) => {
    setNewScopes(prev => prev.includes(scopeId) ? prev.filter(s => s !== scopeId) : [...prev, scopeId]);
  };
  const handleAddApp = () => {
    if (!newName.trim()) return;
    const domains = newDomains.split(',').map(d => d.trim()).filter(Boolean);
    const expiresAt = presetToIsoDate(newExpirationPreset, newCustomExpirationDate);
    const newApp: OAuth2App = {
      id: `oauth-${Date.now()}`,
      name: newName.trim(),
      logo: `https://ui-avatars.com/api/?name=${encodeURIComponent(newName.trim())}&background=E2231A&color=fff&size=32`,
      publicKey: generateKey('pub_key'),
      privateKey: generateKey('prv_key'),
      scopes: newScopes.length > 0 ? newScopes : ['transactions'],
      status: 'active',
      transactionVolume: 0,
      transactionCount: 0,
      domains: domains.length > 0 ? domains : [`${newName.trim().toLowerCase().replace(/\s+/g, '')}.com`],
      connectedDate: new Date().toISOString().split('T')[0],
      lastUsed: 'Just now',
      expiresAt
    };
    setApps(prev => [newApp, ...prev]);
    setNewName('');
    setNewDomains('');
    setNewScopes([]);
    setNewExpirationPreset('never');
    setNewCustomExpirationDate('');
    setShowAddModal(false);
  };
  const filteredApps = apps.filter(app => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = app.name.toLowerCase().includes(q) || app.publicKey.includes(searchQuery) || app.domains.some(d => d.toLowerCase().includes(q));
    return matchesSearch && (statusFilter === 'all' || app.status === statusFilter);
  });
  const totalConnections = apps.length;
  const activeConnections = apps.filter(a => a.status === 'active').length;
  const totalVolume = apps.reduce((s, a) => s + a.transactionVolume, 0);
  return <div className="space-y-5" data-id="element-14692" data-mp="1e9xx2mc18213">
      {/* ─── STATS ─── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-14693" data-mp="19cfleo6mr8pe">
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-center gap-3" data-id="element-14694" data-mp="19a34meclz4dx">
          <div className="h-10 w-10 rounded-lg bg-[#E2231A]/10 flex items-center justify-center flex-shrink-0" data-id="element-14695" data-mp="lkh8jzrj95a5">
            <GlobeIcon size={18} className="text-[#9F140F]" data-id="element-14696" data-mp="vceiz4apvt7t" />
          </div>
          <div data-id="element-14697" data-mp="vdpphbvjrf2m">
            <p className="text-xs text-gray-500" data-id="element-14698" data-mp="ve7twvsd4zlm">
              {t('pages.Connect.total_connections')}
            </p>
            <p className="text-xl font-bold text-gray-900" data-id="element-14699" data-mp="2vu3hh9cwfwk">
              {totalConnections}
            </p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-center gap-3" data-id="element-14700" data-mp="3u1ls2c7hjkh9">
          <div className="h-10 w-10 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0" data-id="element-14701" data-mp="nkd1ht4mvtvq">
            <CheckCircleIcon size={18} className="text-green-600" data-id="element-14702" data-mp="rl6pemi2t8om" />
          </div>
          <div data-id="element-14703" data-mp="weric04tbcgy">
            <p className="text-xs text-gray-500" data-id="element-14704" data-mp="we8axdcsg6h6">
              {t('pages.Connect.active_connections')}
            </p>
            <p className="text-xl font-bold text-gray-900" data-id="element-14705" data-mp="11edvyaojyuqa">
              {activeConnections}
            </p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-4 flex items-center gap-3" data-id="element-14706" data-mp="8n3y21222znn">
          <div className="h-10 w-10 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0" data-id="element-14707" data-mp="dmp754axwoe3">
            <BarChart2Icon size={18} className="text-purple-600" data-id="element-14708" data-mp="in349rb06dxc" />
          </div>
          <div data-id="element-14709" data-mp="1p2x3gnzs33hb">
            <p className="text-xs text-gray-500" data-id="element-14710" data-mp="1p12k3e45njfc">
              {t('pages.Connect.total_volume')}
            </p>
            <p className="text-xl font-bold text-gray-900" data-id="element-14711" data-mp="1tznh74o41mzk">
              {getTenant().currency.code} {totalVolume.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      {/* ─── SEARCH & FILTER ─── */}
      <div className="bg-white rounded-xl border border-gray-200" data-id="element-14712" data-mp="39mioubz2lt80">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-14713" data-mp="34rvah5xs6f75">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-14714" data-mp="2ztlewu859cl0">
            <GlobeIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-14715" data-mp="2vtt2g6fmqmad" />
            {t('pages.Connect.oauth2_connections')}
          </h2>
          <button onClick={() => setShowAddModal(true)} className="px-4 py-2 bg-[#E2231A] hover:bg-[#EB4A42] text-white text-xs font-medium rounded-lg transition-colors flex items-center gap-1.5" data-id="element-14716" data-mp="2qsdi6gla0jt1">
            <PlusIcon size={14} data-id="element-14717" data-mp="k38pn9pp5byl" />
            {t('pages.Connect.add_connection')}
          </button>
        </div>
        <div className="px-6 py-4 flex flex-col sm:flex-row gap-3" data-id="element-14718" data-mp="f3ma2tmebjmm">
          <div className="relative flex-1" data-id="element-14719" data-mp="f2b6ouwv4dw1">
            <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-14720" data-mp="3k3wcn4gqralt" />

            <input type="search" aria-label={t('pages.Connect.attrs.search_by_app_name_api_key')} className={`${inputClass} pl-9`} placeholder={t('pages.Connect.attrs.search_by_app_name_api_key')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-14721" data-mp="3p5d82kggi4v0" />
          </div>
          <div className="flex items-center gap-2" data-id="element-14722" data-mp="36ecd07igitgq">
            <StatusDropdown value={statusFilter} onChange={setStatusFilter} options={[{
            value: 'all',
            label: 'All status'
          }, {
            value: 'active',
            label: 'Active',
            dot: 'bg-yellow-500'
          }, {
            value: 'inactive',
            label: 'Inactive',
            dot: 'bg-red-500'
          }]} data-id="element-14723" data-mp="2sd9o7n02v524" />

            <button className="h-[42px] px-3 border border-gray-200 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-1.5" data-id="element-14724" data-mp="2b1kwuwg9yhs6">
              <RefreshCwIcon size={13} data-id="element-14725" data-mp="2k2stzb721cko" />
              {t('pages.Connect.refresh')}
            </button>
          </div>
        </div>
      </div>

      {/* ─── APP GRID ─── */}
      {filteredApps.length > 0 ? <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-14726" data-mp="2ttvv2czzhrjy">
          {filteredApps.map(app => <div key={app.id} className="bg-white rounded-xl border border-gray-200" data-id="element-14727" data-mp="37ru7hcuqvdwt">
              {/* Header */}
              <div className="px-5 py-3.5 border-b border-gray-200 flex items-center justify-between" data-id="element-14728" data-mp="1df43u3ekqqc8">
                <div className="flex items-center gap-2.5 min-w-0 flex-1" data-id="element-14729" data-mp="18hvu8r7hruml">
                  <div className="h-8 w-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden" data-id="element-14730" data-mp="grtvejnsupp1">
                    <img src={app.logo} alt={app.name} className="w-full h-full object-contain p-1" onError={e => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = `https://ui-avatars.com/api/?name=${app.name}&background=FFC70A&color=fff&size=32`;
              }} data-id="element-14731" data-mp="bsh8y00ubkmz" />
                  </div>
                  <div className="min-w-0" data-id="element-14732" data-mp="6txne0q5sav5">
                    <p className="text-sm font-semibold text-gray-900 truncate" data-id="element-14733" data-mp="6uz1ke2h1dh3">
                      {app.name}
                    </p>
                    <p className="text-[11px] text-gray-500" data-id="element-14734" data-mp="1xfhuyn088hr">
                      {t('pages.Connect.connected')} {app.connectedDate}
                    </p>
                  </div>
                </div>
                <button onClick={() => requestToggle(app.id)} className="relative flex-shrink-0 ml-3" title={app.status === 'active' ? 'Deactivate' : 'Activate'} data-id="element-14735" data-mp="1lr9xwahepjyo">
                  <div className={`w-9 h-5 rounded-full transition-colors ${app.status === 'active' ? 'bg-yellow-500' : 'bg-gray-300'}`} data-id="element-14736" data-mp="1btvrl3p76bk3">
                    <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${app.status === 'active' ? 'translate-x-[18px]' : 'translate-x-0.5'}`} data-id="element-14737" data-mp="24i04a966rowa" />
                  </div>
                </button>
              </div>

              {/* Details — edge-to-edge rows */}
              <div data-id="element-14738" data-mp="1zpgnz4g46fns">
                {/* Public key */}
                <div className="px-5 py-2.5 border-b border-gray-100" data-id="element-14739" data-mp="2ng3yzylbtlek">
                  <div className="flex items-center justify-between mb-1" data-id="element-14740" data-mp="2nesw00zpghoi">
                    <span className="text-xs text-gray-500 flex items-center gap-1" data-id="element-14741" data-mp="2s8nc6ug3qeiu">
                      <KeyIcon size={11} className="text-gray-400" data-id="element-14742" data-mp="322013q2ielfh" />
                      {t('pages.Connect.public_key')}
                    </span>
                    <button onClick={() => copyToClipboard(app.publicKey, `${app.id}-pub`)} aria-label="Copy public key" className="text-gray-400 hover:text-[#9F140F] transition-colors" data-id="element-14743" data-mp="2s54zuzeuq6xi">
                      {copiedKey === `${app.id}-pub` ? <CheckIcon size={12} className="text-green-500" aria-hidden="true" data-id="element-14744" data-mp="2ebnkzrx41idt" /> : <CopyIcon size={12} aria-hidden="true" data-id="element-14745" data-mp="20dpibjs5jae5" />}
                    </button>
                  </div>
                  <code className="text-[11px] text-gray-700 font-mono break-all" data-id="element-14746" data-mp="1vg75vjexed1e">
                    {app.publicKey}
                  </code>
                </div>

                {/* Private key */}
                <div className="px-5 py-2.5 border-b border-gray-100" data-id="element-14747" data-mp="fulls7d6zwlc">
                  <div className="flex items-center justify-between mb-1" data-id="element-14748" data-mp="jx048qcy8uc3">
                    <span className="text-xs text-gray-500 flex items-center gap-1" data-id="element-14749" data-mp="owmhv0wowclf">
                      <LockIcon size={11} className="text-gray-400" data-id="element-14750" data-mp="ou8s97ks3sl9" />
                      {t('pages.Connect.private_key')}
                    </span>
                    <div className="flex items-center gap-1.5" data-id="element-14751" data-mp="3sed2z1chyqxk">
                      <button onClick={() => togglePrivateKey(app.id)} aria-label={showPrivateKeys[app.id] ? 'Hide private key' : 'Show private key'} aria-pressed={!!showPrivateKeys[app.id]} className="text-gray-400 hover:text-[#9F140F] transition-colors" data-id="element-14752" data-mp="17adcecb4spg">
                        {showPrivateKeys[app.id] ? <EyeOffIcon size={12} aria-hidden="true" data-id="element-14753" data-mp="1b6bfkhswrzcv" /> : <EyeIcon size={12} aria-hidden="true" data-id="element-14754" data-mp="1ox87s7ixaeje" />}
                      </button>
                      <button onClick={() => copyToClipboard(app.privateKey, `${app.id}-prv`)} aria-label="Copy private key" className="text-gray-400 hover:text-[#9F140F] transition-colors" data-id="element-14755" data-mp="1k4f3tlva3a8d">
                        {copiedKey === `${app.id}-prv` ? <CheckIcon size={12} className="text-green-500" aria-hidden="true" data-id="element-14756" data-mp="23uh0b4jypocp" /> : <CopyIcon size={12} aria-hidden="true" data-id="element-14757" data-mp="3vhdqj3k6sl6j" />}
                      </button>
                    </div>
                  </div>
                  <code className="text-[11px] text-gray-700 font-mono break-all" data-id="element-14758" data-mp="3ffjsr2nhsnm">
                    {showPrivateKeys[app.id] ? app.privateKey : '••••••••••••••••••••••••••••••'}
                  </code>
                </div>

                {/* Scopes */}
                <div className="px-5 py-2.5 border-b border-gray-100" data-id="element-14759" data-mp="3bv931dod9i8d">
                  <span className="text-xs text-gray-500 block mb-1.5" data-id="element-14760" data-mp="3gt0uy7p0h4ck">
                    {t('pages.Connect.scopes')}
                  </span>
                  <div className="flex flex-wrap gap-1" data-id="element-14761" data-mp="37rjgetr95apz">
                    {app.scopes.map(s => {
                const cfg = scopeConfig[s];
                if (!cfg) return null;
                return <span key={s} className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-50 border border-gray-100 rounded text-[11px] text-gray-700" data-id="element-14762" data-mp="160g3tyxsxm2s">
                          {cfg.icon}
                          {cfg.name}
                        </span>;
              })}
                  </div>
                </div>

                {/* Domains */}
                <div className="px-5 py-2.5 border-b border-gray-100" data-id="element-14763" data-mp="1n9in8oqsxrtz">
                  <span className="text-xs text-gray-500 flex items-center gap-1 mb-1.5" data-id="element-14764" data-mp="1n8pc7bkt8g61">
                    <LinkIcon size={11} className="text-gray-400" data-id="element-14765" data-mp="1iaxm8n3q744h" />
                    {t('pages.Connect.domains')}
                  </span>
                  <div className="flex flex-wrap gap-1" data-id="element-14766" data-mp="18km9qc5z7mlz">
                    {app.domains.map(d => <span key={d} className="inline-flex items-center px-2 py-0.5 bg-[#E2231A]/5 text-[#E2231A] text-[11px] font-medium rounded" data-id="element-14767" data-mp="25zqcc2xo2hr6">
                        {d}
                      </span>)}
                  </div>
                </div>

                {/* Expiration */}
                <div className="px-5 py-2.5 border-b border-gray-100 flex items-center justify-between" data-id="element-14768" data-mp="2dlcaas6frp29">
                  <span className="text-xs text-gray-500" data-id="element-14769" data-mp="2nhp2tmq8vwvf">Expiration</span>
                  {app.expiresAt ? (() => {
              const {
                label,
                tone
              } = formatExpiration(app.expiresAt);
              const toneClass = tone === 'expired' ? 'text-red-600' : tone === 'soon' ? 'text-amber-600' : 'text-gray-700';
              return <span className={`text-[11px] font-medium ${toneClass}`} data-id="element-14770" data-mp="3b7t61szitulw">
                          {label}
                        </span>;
            })() : <span className="text-[11px] text-gray-400" data-id="element-14771" data-mp="4ga0zy6ql85q">
                      Never expires
                    </span>}
                </div>

                {/* Volume & count */}
                <div className="grid grid-cols-2" data-id="element-14772" data-mp="ea8xagk4pxpr">
                  <div className="px-5 py-2.5 border-b border-gray-100 border-r border-gray-100" data-id="element-14773" data-mp="9etpa58syw19">
                    <span className="text-xs text-gray-500" data-id="element-14774" data-mp="3hu3liwq0h714">
                      {t('pages.Connect.volume')}
                    </span>
                    <p className="text-xs font-medium text-gray-900" data-id="element-14775" data-mp="3cse8jbj8ajae">
                      {getTenant().currency.code}{' '}
                      {app.transactionVolume.toLocaleString()}
                    </p>
                  </div>
                  <div className="px-5 py-2.5 border-b border-gray-100" data-id="element-14776" data-mp="2z2aq6vngz9q8">
                    <span className="text-xs text-gray-500" data-id="element-14777" data-mp="2u3r670sfs80i">
                      {t('pages.Connect.transactions')}
                    </span>
                    <p className="text-xs font-medium text-gray-900" data-id="element-14778" data-mp="19knmwq2hmqpn">
                      {app.transactionCount.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-5 py-3 flex items-center justify-between" data-id="element-14779" data-mp="10j7dolnesun0">
                <span className="text-[11px] text-gray-400" data-id="element-14780" data-mp="14mvkh247vm77">
                  {t('pages.Connect.last_used')} {app.lastUsed}
                </span>
                <button onClick={() => openEditModal(app.id)} className="text-xs font-medium text-[#9F140F] hover:text-[#8C6C00] transition-colors flex items-center gap-1" data-id="element-14781" data-mp="1xbt2d44ingzf">
                  <SettingsIcon size={11} data-id="element-14782" data-mp="1o9a1uqo7dggc" />
                  {t('pages.Connect.settings')}
                </button>
              </div>
            </div>)}
        </div> : <div className="bg-white rounded-xl border border-gray-200 py-16 flex flex-col items-center text-center" data-id="element-14783" data-mp="cwhctnmsr63z">
          <GlobeIcon size={24} className="text-gray-400 mb-3" data-id="element-14784" data-mp="372slhyfqiw4h" />
          <p className="text-sm font-medium text-gray-700" data-id="element-14785" data-mp="3c25fd350x3gk">
            {t('pages.Connect.no_connections_found')}
          </p>
          <p className="text-xs text-gray-500 mt-1 max-w-xs" data-id="element-14786" data-mp="3h32xxgty3zil">
            {t('pages.Connect.no_connections_match_your_search_criteria')}
          </p>
        </div>}

      {/* ─── ADD CONNECTION MODAL ─── */}
      {showAddModal && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{
      margin: 0,
      background: 'rgba(17,24,39,0.5)'
    }} onClick={e => {
      if (e.target === e.currentTarget) setShowAddModal(false);
    }} data-id="element-14787" data-mp="2tbxg4w4g2x5n">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden" data-id="element-14788" data-mp="25ltg0yqgtfje">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0" data-id="element-14789" data-mp="29mddk3eyal62">
                <h3 className="text-base font-semibold text-gray-900" data-id="element-14790" data-mp="222b4ft5wkx8d">
                  {t('pages.Connect.add_connection')}
                </h3>
                <button onClick={() => setShowAddModal(false)} aria-label="Close" className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-14791" data-mp="1t2xrurmhfsjf">
                  <XIcon size={18} aria-hidden="true" data-id="element-14792" data-mp="1j7dxwarhb3xb" />
                </button>
              </div>

              <div className="p-6 space-y-4 overflow-y-auto flex-1 min-h-0" data-id="element-14793" data-mp="1edjhphb3172z">
                {/* App name */}
                <div data-id="element-14794" data-mp="19g9khfhgbr4l">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-14795" data-mp="19dnal9738zj7">
                    {t('pages.Connect.app_name')}{' '}
                    <span className="text-red-400" data-id="element-14796" data-mp="10es744vfi3jl">*</span>
                  </label>
                  <input type="text" className={inputClass} placeholder={t('pages.Connect.attrs.eg_opera_pms_salesforce')} value={newName} onChange={e => setNewName(e.target.value)} autoFocus data-id="element-14797" data-mp="vh9vu7txvc8f" />
                </div>

                {/* Domains */}
                <div data-id="element-14798" data-mp="gvm8kdsfvrlc">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-14799" data-mp="14j448ketr9tl">
                    {t('pages.Connect.authorized_domains')}
                  </label>
                  <input type="text" className={inputClass} placeholder={t('pages.Connect.attrs.examplecom_apiexamplecom')} value={newDomains} onChange={e => setNewDomains(e.target.value)} data-id="element-14800" data-mp="utau7nubz2rg" />

                  <p className="text-[11px] text-gray-400 mt-1" data-id="element-14801" data-mp="1s9gj6f8hm6to">
                    {t('pages.Connect.commaseparated_list_of_domains')}
                  </p>
                </div>

                {/* Scopes */}
                <div data-id="element-14802" data-mp="37zqxq22ywfsg">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-14803" data-mp="37y6dp6ea4f8u">
                    {t('pages.Connect.scopes')}
                  </label>
                  <div className="grid grid-cols-2 gap-1.5" data-id="element-14804" data-mp="3hqlui0duaxs5">
                    {Object.entries(scopeConfig).map(([id, cfg]) => <button key={id} type="button" onClick={() => toggleScope(id)} className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border transition-colors text-left ${newScopes.includes(id) ? 'border-[#E2231A] bg-[#E2231A]/[0.03]' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-14805" data-mp="2p1emmashwvu8">
                        <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 ${newScopes.includes(id) ? 'bg-[#E2231A] border-[#E2231A]' : 'border-gray-300'}`} data-id="element-14806" data-mp="e9mz4u4y7myd">
                          {newScopes.includes(id) && <CheckIcon size={9} className="text-white" data-id="element-14807" data-mp="1ijoxhif2y47o" />}
                        </div>
                        <div className="flex items-center gap-1" data-id="element-14808" data-mp="1nkmfnv03ay96">
                          {cfg.icon}
                          <span className="text-xs text-gray-700" data-id="element-14809" data-mp="1shmilx1n7kw2">
                            {cfg.name}
                          </span>
                        </div>
                      </button>)}
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2" data-id="element-14810" data-mp="2687vn54f10m1">
                <button onClick={() => setShowAddModal(false)} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg transition-colors" data-id="element-14811" data-mp="2b9ot0qnoy4xv">
                  {t('pages.Connect.cancel')}
                </button>
                <button onClick={handleAddApp} disabled={!newName.trim()} className={`px-5 py-2 text-sm font-medium rounded-lg transition-colors shadow-sm ${newName.trim() ? 'bg-[#E2231A] hover:bg-[#EB4A42] text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-14812" data-mp="3vqerrmgl5sff">
                  {t('pages.Connect.create_connection')}
                </button>
              </div>
            </div>
          </div>, document.body)}

      {/* ─── TOGGLE CONFIRMATION MODAL ─── */}
      {pendingApp && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{
      margin: 0,
      background: 'rgba(17,24,39,0.5)'
    }} onClick={e => {
      if (e.target === e.currentTarget) setPendingToggle(null);
    }} data-id="element-14813" data-mp="8kxbwbxmvsda">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-sm overflow-hidden" data-id="element-14814" data-mp="2vkktr0iznvqi">
              <div className="p-6 text-center" data-id="element-14815" data-mp="30grnzw20vism">
                <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4 ${pendingApp.status === 'active' ? 'bg-red-50' : 'bg-green-50'}`} data-id="element-14816" data-mp="2lt6p6ec6k5qi">
                  {pendingApp.status === 'active' ? <XCircleIcon size={24} className="text-red-500" data-id="element-14817" data-mp="2cu34j5xd7i9w" /> : <CheckCircleIcon size={24} className="text-green-500" data-id="element-14818" data-mp="2grh9umhxd5wm" />}
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-1" data-id="element-14819" data-mp="22w5dkao4f7dl">
                  {pendingApp.status === 'active' ? 'Deactivate' : 'Activate'}{' '}
                  {pendingApp.name}?
                </h3>
                <p className="text-sm text-gray-500" data-id="element-14820" data-mp="61ae6npb51ux">
                  {pendingApp.status === 'active' ? 'This will revoke API access for this connection. All active sessions will be terminated.' : 'This will restore API access for this connection and allow new transactions.'}
                </p>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2" data-id="element-14821" data-mp="20apcbg0hacrg">
                <button onClick={() => setPendingToggle(null)} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg transition-colors" data-id="element-14822" data-mp="1vh35i4gzx33k">
                  {t('pages.Connect.cancel')}
                </button>
                <button onClick={confirmToggle} className={`px-5 py-2 text-sm font-medium rounded-lg transition-colors shadow-sm ${pendingApp.status === 'active' ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-green-600 hover:bg-green-700 text-white'}`} data-id="element-14823" data-mp="2j6xhkjthqnmr">
                  {pendingApp.status === 'active' ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            </div>
          </div>, document.body)}

      {/* ─── EDIT CONNECTION MODAL ─── */}
      {editingApp && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{
      margin: 0,
      background: 'rgba(17,24,39,0.5)'
    }} onClick={e => {
      if (e.target === e.currentTarget) setEditingApp(null);
    }} data-id="element-14824" data-mp="7stmfh1egpw1">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden" data-id="element-14825" data-mp="3g9od7y4xajtr">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0" data-id="element-14826" data-mp="3p8a3ygkjt55m">
                <h3 className="text-base font-semibold text-gray-900" data-id="element-14827" data-mp="10gz3ntt8vwr4">
                  {t('pages.Connect.edit_connection')}
                </h3>
                <button onClick={() => setEditingApp(null)} aria-label="Close" className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-14828" data-mp="14g7xeyoabumo">
                  <XIcon size={18} aria-hidden="true" data-id="element-14829" data-mp="2l0l0qf1wgaxn" />
                </button>
              </div>

              <div className="p-6 space-y-4 overflow-y-auto flex-1 min-h-0" data-id="element-14830" data-mp="2g5nn0utoddef">
                {/* App name */}
                <div data-id="element-14831" data-mp="269kjbkj6d6pd">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-14832" data-mp="26a3ri9j0wgq7">
                    {t('pages.Connect.app_name')}{' '}
                    <span className="text-red-400" data-id="element-14833" data-mp="21clo0ynrytpl">*</span>
                  </label>
                  <input type="text" className={inputClass} placeholder={t('pages.Connect.attrs.eg_opera_pms_salesforce')} value={editName} onChange={e => setEditName(e.target.value)} autoFocus data-id="element-14834" data-mp="1x95uib7059h4" />
                </div>

                {/* Domains */}
                <div data-id="element-14835" data-mp="2vsahgrw54gv3">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-14836" data-mp="2vrr9a2wal6u9">
                    {t('pages.Connect.authorized_domains')}
                  </label>
                  <input type="text" className={inputClass} placeholder={t('pages.Connect.attrs.examplecom_apiexamplecom')} value={editDomains} onChange={e => setEditDomains(e.target.value)} data-id="element-14837" data-mp="2mrujtz22w3y5" />

                  <p className="text-[11px] text-gray-400 mt-1" data-id="element-14838" data-mp="39ppdwgkt9owk">
                    {t('pages.Connect.commaseparated_list_of_domains')}
                  </p>
                </div>

                {/* Scopes */}
                <div data-id="element-14839" data-mp="1c07j1ez28o8">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-14840" data-mp="1bgofhdl5e8m">
                    {t('pages.Connect.scopes')}
                  </label>
                  <div className="grid grid-cols-2 gap-1.5" data-id="element-14841" data-mp="1oxte02u12pbk">
                    {Object.entries(scopeConfig).map(([id, cfg]) => <button key={id} type="button" onClick={() => toggleEditScope(id)} className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border transition-colors text-left ${editScopes.includes(id) ? 'border-[#E2231A] bg-[#E2231A]/[0.03]' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-14842" data-mp="1sydcb9qc7yza">
                        <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 ${editScopes.includes(id) ? 'bg-[#E2231A] border-[#E2231A]' : 'border-gray-300'}`} data-id="element-14843" data-mp="2cnxl4k6fd77b">
                          {editScopes.includes(id) && <CheckIcon size={9} className="text-white" data-id="element-14844" data-mp="2qnggece50o27" />}
                        </div>
                        <div className="flex items-center gap-1" data-id="element-14845" data-mp="llujy548tgh7">
                          {cfg.icon}
                          <span className="text-xs text-gray-700" data-id="element-14846" data-mp="10aqpnvituhen">
                            {cfg.name}
                          </span>
                        </div>
                      </button>)}
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2 flex-shrink-0" data-id="element-14847" data-mp="19d9qka31yjy9">
                <button onClick={() => setEditingApp(null)} className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg transition-colors" data-id="element-14848" data-mp="15c67zftme9tc">
                  {t('pages.Connect.cancel')}
                </button>
                <button onClick={handleSaveEdit} disabled={!editName.trim()} className={`px-5 py-2 text-sm font-medium rounded-lg transition-colors shadow-sm ${editName.trim() ? 'bg-[#E2231A] hover:bg-[#EB4A42] text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-14849" data-mp="3l32ai3qrwang">
                  {t('pages.Connect.save_changes')}
                </button>
              </div>
            </div>
          </div>, document.body)}
    </div>;
}